import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users } from "lucide-react";

export function GroupThreadDialog({ recipients, onCreated }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState({});
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [q, setQ] = useState("");
  const admins = recipients.filter((r) => r.role === "admin");
  const customers = recipients.filter((r) => r.role !== "admin" && `${r.name} ${r.company || ""}`.toLowerCase().includes(q.toLowerCase()));
  const ids = Object.keys(picked).filter((k) => picked[k]);
  const create = async () => {
    if (!body.trim()) { toast.error("Write the first message"); return; }
    if (!ids.some((id) => recipients.find((r) => r.id === id)?.role !== "admin")) { toast.error("Pick at least one customer"); return; }
    try {
      const { data } = await api.post("/threads/group", { participant_ids: ids, title: title || null, body });
      toast.success("Group conversation started");
      setOpen(false); setPicked({}); setTitle(""); setBody("");
      onCreated(data.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const Row = ({ r, sub }) => (
    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`group-pick-${r.id}`}>
      <Checkbox checked={!!picked[r.id]} onCheckedChange={(v) => setPicked((p) => ({ ...p, [r.id]: !!v }))} />
      <span className="flex-1 truncate"><b>{r.name}</b> <span className="text-slate-500 text-xs">· {sub}</span></span>
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="group-thread-button"><Users className="h-4 w-4 mr-2" />Group conversation</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="group-thread-dialog">
        <DialogHeader><DialogTitle>Start a group conversation</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-600 -mt-1">Everyone you pick shares one thread — e.g. you, Jennifer and a customer. You're included automatically.</p>
        <div className="space-y-3">
          {admins.length > 0 && <div><div className="tag-mono text-slate-500 mb-1">SAFER HIGHWAYS TEAM</div>{admins.map((r) => <Row key={r.id} r={r} sub="Admin" />)}</div>}
          <div>
            <div className="flex items-center justify-between mb-1"><div className="tag-mono text-slate-500">CUSTOMERS</div><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-7 w-40 text-xs" data-testid="group-search" /></div>
            <div className="max-h-44 overflow-y-auto rounded border border-slate-200">{customers.map((r) => <Row key={r.id} r={r} sub={r.role === "terminal_manager" ? `${r.terminal_name || "Terminal manager"} (${r.company})` : r.shared ? `${r.company} · team member` : r.company || r.email} />)}{customers.length === 0 && <div className="p-3 text-xs text-slate-400">No matches</div>}</div>
          </div>
          <div><Label className="text-xs">Group name (optional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Test Fleet · onboarding" data-testid="group-title-input" /></div>
          <div><Label className="text-xs">First message</Label><Input value={body} onChange={(e) => setBody(e.target.value)} data-testid="group-first-message" /></div>
          <DialogFooter><Button className="bg-blue-700 hover:bg-blue-800" onClick={create} disabled={ids.length < 1} data-testid="group-create-button">Start ({ids.length + 1} people)</Button></DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
